import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash } from "@phosphor-icons/react";

export default function Fleet() {
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState("trucks");
  const [showAdd, setShowAdd] = useState(false);
  const emptyT = { unit_number: "", customer_id: "", make: "", model: "", year: 2024, vin: "", status: "active" };
  const emptyD = { name: "", customer_id: "", license_number: "", email: "", phone: "", status: "active" };
  const [form, setForm] = useState(emptyT);

  const load = async () => {
    const [t, d, c] = await Promise.all([api.get("/trucks"), api.get("/drivers"), api.get("/customers")]);
    setTrucks(t.data); setDrivers(d.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (tab === "trucks") { await api.post("/trucks", form); }
      else { await api.post("/drivers", form); }
      toast.success("Added"); setShowAdd(false);
      setForm(tab === "trucks" ? emptyT : emptyD); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (kind, id) => {
    await api.delete(`/${kind}/${id}`); toast.success("Deleted"); load();
  };

  return (
    <SectionPad>
      <PageHeader
        title="Fleet"
        subtitle="REGISTRY / TRUCKS &amp; DRIVERS"
        testId="fleet-header"
        actions={
          <button className="gf-btn gf-btn-primary" onClick={() => { setForm(tab === "trucks" ? emptyT : emptyD); setShowAdd(true); }} data-testid="add-fleet-button">
            <Plus size={14}/> Add {tab === "trucks" ? "truck" : "driver"}
          </button>
        }
      />
      <div className="flex gap-1 mb-4" data-testid="fleet-tabs">
        {["trucks", "drivers"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`fleet-tab-${t}`}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border ${tab === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}
          >
            {t} ({t === "trucks" ? trucks.length : drivers.length})
          </button>
        ))}
      </div>

      <div className="gf-card">
        {tab === "trucks" ? (
          <table className="gf-table">
            <thead><tr><th>UNIT</th><th>CUSTOMER</th><th>MAKE/MODEL</th><th>YEAR</th><th>VIN</th><th>STATUS</th><th></th></tr></thead>
            <tbody>
              {trucks.map((r) => (
                <tr key={r.truck_id}>
                  <td className="font-mono font-semibold">{r.unit_number}</td>
                  <td>{customers.find((c) => c.customer_id === r.customer_id)?.company_name || "—"}</td>
                  <td className="text-xs">{r.make} {r.model}</td>
                  <td className="font-mono">{r.year}</td>
                  <td className="font-mono text-xs text-zinc-500 truncate" style={{ maxWidth: 180 }}>{r.vin}</td>
                  <td><Badge variant={r.status === "active" ? "success" : "muted"}>{r.status}</Badge></td>
                  <td><button className="gf-btn gf-btn-ghost" onClick={() => del("trucks", r.truck_id)}><Trash size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="gf-table">
            <thead><tr><th>DRIVER</th><th>CUSTOMER</th><th>LICENSE</th><th>CONTACT</th><th>STATUS</th><th></th></tr></thead>
            <tbody>
              {drivers.map((r) => (
                <tr key={r.driver_id}>
                  <td className="font-medium">{r.name}</td>
                  <td>{customers.find((c) => c.customer_id === r.customer_id)?.company_name || "—"}</td>
                  <td className="font-mono text-xs">{r.license_number}</td>
                  <td className="text-xs">{r.email}<div className="text-zinc-500">{r.phone}</div></td>
                  <td><Badge variant={r.status === "active" ? "success" : "muted"}>{r.status}</Badge></td>
                  <td><button className="gf-btn gf-btn-ghost" onClick={() => del("drivers", r.driver_id)}><Trash size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>New {tab === "trucks" ? "truck" : "driver"}</h3></div>
            <div className="gf-card-body space-y-3">
              {tab === "trucks" ? (
                <>
                  <div><div className="overline mb-1">Unit number</div><input className="gf-input" value={form.unit_number || ""} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} /></div>
                  <div><div className="overline mb-1">Customer</div><select className="gf-input" value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                    <option value="">— unassigned —</option>
                    {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                  </select></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><div className="overline mb-1">Make</div><input className="gf-input" value={form.make || ""} onChange={(e) => setForm({ ...form, make: e.target.value })} /></div>
                    <div><div className="overline mb-1">Model</div><input className="gf-input" value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
                    <div><div className="overline mb-1">Year</div><input type="number" className="gf-input" value={form.year || 2024} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 2024 })} /></div>
                  </div>
                  <div><div className="overline mb-1">VIN</div><input className="gf-input" value={form.vin || ""} onChange={(e) => setForm({ ...form, vin: e.target.value })} /></div>
                </>
              ) : (
                <>
                  <div><div className="overline mb-1">Name</div><input className="gf-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><div className="overline mb-1">Customer</div><select className="gf-input" value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                    <option value="">— unassigned —</option>
                    {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.company_name}</option>)}
                  </select></div>
                  <div><div className="overline mb-1">License #</div><input className="gf-input" value={form.license_number || ""} onChange={(e) => setForm({ ...form, license_number: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><div className="overline mb-1">Email</div><input className="gf-input" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                    <div><div className="overline mb-1">Phone</div><input className="gf-input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={save} data-testid="save-fleet-button">Create</button>
            </div>
          </div>
        </div>
      )}
    </SectionPad>
  );
}
